import { SimpleRow } from '@/typings/Table'
import type { components } from '@/api/typeApi'

export const NODE_TYPES = {
  CONTROL_PLANE: 'ControlPlane',
  WORKER: 'Worker',
} as const

export type NodeType = (typeof NODE_TYPES)[keyof typeof NODE_TYPES]

export type ClusterDto = components['schemas']['ClusterDto']

export type ExtendedClusterDto = components['schemas']['ExtendedClusterDto']

export type DatacenterDto = components['schemas']['DatacenterDto']

export type CreateClusterRequest = components['schemas']['CreateClusterRequest']

export type UpdateClusterRequest = components['schemas']['UpdateClusterRequest']

export type ClusterNodeDto = components['schemas']['ClusterNodeDto']

export type CreateClusterResponse =
  components['schemas']['CreateClusterResponse']

export interface CreateClusterFormData {
  name: string
  datacenterId: string
  vlan?: number
  talosVersion: string
  servers: Array<{
    serverId: string
    installDisk: string
    workloadInterface: string
    workloadDisks: string[]
    role: 'control-plane' | 'worker'
    selected: boolean
  }>
}

export function mapFormDataToCreateRequest(
  formData: CreateClusterFormData,
): CreateClusterRequest {
  const request: CreateClusterRequest = {
    name: formData.name,
    datacenterId: formData.datacenterId,
    transportVlan: formData.vlan,
    nodes: formData.servers
      .filter((server) => server.selected)
      .map((server) => ({
        nodeId: server.serverId,
        installDisk: server.installDisk,
        workloadInterface: server.workloadInterface,
        workloadDisks: server.workloadDisks,
        nodeType:
          server.role === 'control-plane'
            ? NODE_TYPES.CONTROL_PLANE
            : NODE_TYPES.WORKER,
      })),
  }

  return request
}

export interface ClusterRow {
  id: string
  name: string
  dataCenter: DatacenterDto | null
  controlPlane: number
  workerNode: number
  k8sVersion: string
  subnetsCount: number
  status: string
  chainAddress?: string | null
  // Carried straight from ExtendedClusterDto (the list row is spread as-is).
  transportVlan?: number | null
  // True while a saved VLAN change has not yet been delivered to the cluster.
  pendingConfig: boolean
}

export type ClusterRecord = ClusterRow & SimpleRow

export interface ServerForClusterRow {
  id: string
  name: string
  address?: string
  nodeAddresses?: string[]
  talosIp?: string
  talosVersion?: string | null
  disks: {
    id: string
    type?: string | undefined
    size?: number | undefined
    io_size?: number | undefined
    pretty_size?: string | undefined
    model?: string | undefined
  }[]
  interfaces: {
    id: string
    link_state?: boolean
    driver?: string
    product?: string
    vendor?: string
    hardware_addr?: string
  }[]
  lastTalosConnection?: string | null
  disabled?: boolean
}

export type ServerForClusterRecord = ServerForClusterRow & SimpleRow

export function mapClusterDtoToClusterRow(datacenters: DatacenterDto[]) {
  const datacenterMap = new Map(
    datacenters.map((datacenter) => [datacenter.id, datacenter]),
  )

  return (dto: ExtendedClusterDto): ClusterRow => ({
    ...dto,
    dataCenter: datacenterMap.get(dto.datacenterId) ?? null,
    controlPlane: dto.controlNodesCount,
    workerNode: dto.workerNodesCount,
    k8sVersion: '-',
  })
}
